
/* ============================== src/components/Calendar.jsx ============================== */
// path: frontend/src/components/Calendar.jsx
/**
 * Вкладка «Календарь»: день / неделя / месяц, задачи по сроку.
 *
 * RBAC:
 *   - canSeeAllContent → capabilities.seeAllContent (useAuth);
 *   - «только мои» — UI-фильтр isMyTask (inline): не RBAC, а UX.
 *
 * useScheduleDb отдаёт /tasks уже scope-фильтрованным, поэтому
 * computeScope/taskVisible здесь не нужны.
 */
import {
  useState,
  useMemo,
  useLayoutEffect,
  useRef,
  useCallback,
  memo,
} from "react";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../hooks/useAuth";
import { buildTaskMenu } from "./menus";
import FloatingMenu from "./FloatingMenu";
import EmployeeCardLink from "./EmployeeCardLink";
import { Clickable } from "./Clickable";
import { Ic } from "./Icons";
import { ICONS } from "./iconsData";
import { ToggleSwitch } from "./ToggleSwitch";
import { fmtDMY, iso, addDays } from "../utils/date";
import { useScheduleDb } from "../hooks/useDb";
import { useTaskActions } from "../hooks/useTaskActions";
import { ColorDot, ColoredSpan } from "./ColoredSpan";
import { taskWord } from "../utils/pluralize";

const MONTH_NAMES = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

const MIN_CELL_HEIGHT = 80;
const CHIP_LIMIT_COMPACT = 3;
const CHIP_LIMIT_WIDE = 12;

const isMyTask = (task, userId) => !!task && task.assigneeId === userId;

const DayCell = ({ d, big, byDay, renderChip, onDayClick, todayIso }) => {
  const dayIso = iso(d);
  const tasks = byDay[dayIso] || [];
  const limit = big ? CHIP_LIMIT_WIDE : CHIP_LIMIT_COMPACT;
  const visible = tasks.slice(0, limit);
  const hiddenCount = tasks.length - visible.length;
  const isToday = dayIso === todayIso;

  return (
    <div className={`cal-cell${isToday ? " today" : ""}`}>
      <button
        type="button"
        className="cal-daynum"
        onClick={() => onDayClick(d)}
        aria-label={`Открыть день ${fmtDMY(dayIso)}`}
        title="Открыть день"
      >
        {d.getDate()}
      </button>
      <div className="cal-chips">
        {visible.map((t) => renderChip(t))}
        {hiddenCount > 0 && (
          <button
            type="button"
            className="st-chip st-chip-toggle cal-more"
            onClick={() => onDayClick(d)}
            aria-label={`Открыть день ${fmtDMY(dayIso)}: ещё ${hiddenCount} ${taskWord(hiddenCount)}`}
            title="Открыть день"
          >
            {`+${hiddenCount} ${taskWord(hiddenCount)}`}
          </button>
        )}
      </div>
    </div>
  );
};

function Calendar({ ur, openTask, openCopyTask, openTemplateFromTask }) {
  const { capabilities } = useAuth();
  const db = useScheduleDb();
  const { tasks, projects, employees } = db;

  const { showToast } = useToast();

  const [mode, setMode] = useState("month");
  const [anchor, setAnchor] = useState(new Date());
  const [showAll, setShowAll] = useState(false);

  const canSeeAll = Boolean(capabilities.seeAllContent);

  const todayIso = iso(new Date());

  const { handleMove: handleMoveTask, handleDelete: handleDeleteTask } =
    useTaskActions({ ur, db, toast: showToast });

  const allTasks = useMemo(() => {
    let list = tasks.filter(
      (t) =>
        t.isActive && t.deadline && !["closed", "cancelled"].includes(t.status),
    );
    if (canSeeAll && !showAll) list = list.filter((t) => isMyTask(t, ur.id));
    return list;
  }, [tasks, ur.id, canSeeAll, showAll]);

  const byDay = useMemo(() => {
    const m = {};
    allTasks.forEach((t) => {
      (m[t.deadline] = m[t.deadline] || []).push(t);
    });
    return m;
  }, [allTasks]);

  const panelRef = useRef(null);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const measure = () => {
      panel.style.setProperty("--cal-cell-min-h", "0px");
      void panel.offsetHeight;

      let max = 0;
      panel.querySelectorAll(".cal-cell").forEach((cell) => {
        const h = cell.offsetHeight;
        if (h > max) max = h;
      });

      panel.style.setProperty(
        "--cal-cell-min-h",
        `${Math.max(max, MIN_CELL_HEIGHT)}px`,
      );
    };

    measure();

    let frameId = 0;
    const onResize = () => {
      if (frameId) return;
      frameId = requestAnimationFrame(() => {
        frameId = 0;
        measure();
      });
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [byDay, mode, anchor, showAll]);

  const openDay = useCallback((d) => {
    setAnchor(d);
    setMode("day");
  }, []);

  const shift = (dir) => {
    if (mode === "month") {
      setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));
    } else if (mode === "week") {
      setAnchor(addDays(anchor, dir * 7));
    } else {
      setAnchor(addDays(anchor, dir));
    }
  };

  const title =
    mode === "month"
      ? `${MONTH_NAMES[anchor.getMonth()]} ${anchor.getFullYear()}`
      : mode === "week"
        ? `Неделя ${fmtDMY(iso(addDays(anchor, -((anchor.getDay() + 6) % 7))))} - ${fmtDMY(iso(addDays(anchor, 6 - ((anchor.getDay() + 6) % 7))))}`
        : fmtDMY(iso(anchor));

  const renderTaskChip = (task) => {
    const p = projects.find((x) => x.id === task.projectId);
    const assignee = task.assigneeId
      ? employees.find((e) => e.id === task.assigneeId)
      : null;
    const menuItems = buildTaskMenu({
      task,
      user: ur,
      capabilities,
      db,
      openTask,
      onMove: handleMoveTask,
      onDelete: handleDeleteTask,
      onCopy: openCopyTask,
      onMakeTemplate: openTemplateFromTask,
    });

    return (
      <FloatingMenu key={task.id} items={menuItems}>
        {({ anchorProps }) => (
          <ColoredSpan color={p?.color} className="cal-chip-wrap">
            <Clickable
              as="div"
              {...anchorProps}
              className="cal-chip"
              onClick={() => openTask(task.id)}
              title={`${task.title} (${p?.code || "без проекта"})`}
            >
              <div className="cal-task-title">
                <ColorDot color={p?.color} className="pdot" />
                <span>{task.title}</span>
              </div>
              <div className="cal-executors">
                {assignee && <EmployeeCardLink employee={assignee} size="xs" />}
              </div>
            </Clickable>
          </ColoredSpan>
        )}
      </FloatingMenu>
    );
  };

  const renderBody = () => {
    if (mode === "month") {
      const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const offset = (first.getDay() + 6) % 7;
      const cells = [];
      for (let i = 0; i < 42; i++) cells.push(addDays(first, i - offset));
      return (
        <>
          <div className="cal-grid-head">
            {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"].map((w) => (
              <div key={w} className="cal-wd">
                {w}
              </div>
            ))}
          </div>
          <div className="cal-grid">
            {cells.map((d) => (
              <div
                key={iso(d)}
                className={d.getMonth() === anchor.getMonth() ? "" : "outwrap"}
              >
                <DayCell
                  d={d}
                  big={false}
                  byDay={byDay}
                  renderChip={renderTaskChip}
                  onDayClick={openDay}
                  todayIso={todayIso}
                />
              </div>
            ))}
          </div>
        </>
      );
    }
    if (mode === "week") {
      const mon = addDays(anchor, -((anchor.getDay() + 6) % 7));
      return (
        <div className="cal-week">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <DayCell
              key={i}
              d={addDays(mon, i)}
              big
              byDay={byDay}
              renderChip={renderTaskChip}
              onDayClick={openDay}
              todayIso={todayIso}
            />
          ))}
        </div>
      );
    }
    return (
      <div className="cal-week one">
        <DayCell
          d={anchor}
          big
          byDay={byDay}
          renderChip={renderTaskChip}
          onDayClick={openDay}
          todayIso={todayIso}
        />
      </div>
    );
  };

  return (
    <div className="cal-panel" ref={panelRef}>
      <div className="cal-head">
        <div className="cal-nav">
          <button className="icon-btn" onClick={() => shift(-1)}>
            <Ic d={ICONS.left} size={16} />
          </button>
          <div className="cal-title">{title}</div>
          <button className="icon-btn" onClick={() => shift(1)}>
            <Ic d={ICONS.right} size={16} />
          </button>
        </div>
        <div className="cal-right">
          <button
            className="btn ghost sm"
            onClick={() => setAnchor(new Date())}
          >
            Сегодня
          </button>
          <div className="seg">
            {["day", "week", "month"].map((m) => (
              <button
                key={m}
                className={`seg-btn${mode === m ? " on" : ""}`}
                onClick={() => setMode(m)}
              >
                {
                  ["День", "Неделя", "Месяц"][
                    ["day", "week", "month"].indexOf(m)
                  ]
                }
              </button>
            ))}
          </div>
          {canSeeAll && (
            <ToggleSwitch
              checked={showAll}
              onChange={setShowAll}
              label="Все задачи"
            />
          )}
        </div>
      </div>
      <div className="cal-note">Только задачи со сроком выполнения.</div>
      {renderBody()}
    </div>
  );
}

export default memo(Calendar);
